/* ===========================
IMPORTS
=========================== */
import React from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
// IMPORT - STYLING
import '../../assets/css/Tables.css';
// IMPORT - COMPONENTS
import ButtonDropdownLimits from './ButtonDropdownLimits';
import ButtonDropdownColumns from './ButtonDropdownColumns';
import Loader from './Loader';
import {
  IconSearch,
  IconArrowDown,
  IconArrowUp,
  IconRadioUnchecked,
  IconRadioPartialChecked,
  IconRadioChecked,
} from './Icons';

/* ===========================
CLASS
=========================== */
class Tables extends React.Component {
  /* ===========================
  DEFAULT
  =========================== */
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.state = {
      data: this.props.data,
      columns: this.props.columns,
      loading: this.props.loading,
      columnsCount: ((Object.keys(this.props.columns)
        .filter((item) => this.props.columns[item].visible)).length + 1),
      showSearch: ((typeof this.props.optionsShowSearch === 'boolean') ? this.props.optionsShowSearch : true),
      showSelection: ((typeof this.props.optionsShowSelection === 'boolean') ? this.props.optionsShowSelection : true),
      showPagination: ((typeof this.props.optionsShowPagination === 'boolean') ? this.props.optionsShowPagination : true),
      showResults: ((typeof this.props.optionsShowResults === 'boolean') ? this.props.optionsShowResults : true),
      showColumns: ((typeof this.props.optionsShowColumns === 'boolean') ? this.props.optionsShowColumns : true),
      showLimit: ((typeof this.props.optionsShowLimit === 'boolean') ? this.props.optionsShowLimit : true),
      showSearchColumn: ((typeof this.props.optionsShowSearchColumn === 'boolean') ? this.props.optionsShowSearchColumn : true),
      showOptionsRow: null,
      showSelectionOptions: false,
      showSortDirection: ((typeof this.props.optionsSortDirection === 'boolean') ? this.props.optionsSortDirection : true),
      selection: 'none',
      selections: [],
      prevProps: {},
    };

    this.onSearchColumnUpdate = this.onSearchColumnUpdate.bind(this);
    this.onSearchUpdate = this.onSearchUpdate.bind(this);
    this.onClickPrev = this.onClickPrev.bind(this);
    this.onClickNext = this.onClickNext.bind(this);
    this.onClickRowOptions = this.onClickRowOptions.bind(this);
    this.onClickRowCheckbox = this.onClickRowCheckbox.bind(this);
    this.onClickSelectionsDropdown = this.onClickSelectionsDropdown.bind(this);
    this.onClickSelectionCheckbox = this.onClickSelectionCheckbox.bind(this);
    this.onClickSort = this.onClickSort.bind(this);
  }

  /**
   * @param {Object} nextProps
   * @param state
   */
  static getDerivedStateFromProps(nextProps, state) {
    const { prevProps } = state;

    let hasDifferentData = false;

    prevProps.data?.forEach((datum, index) => {
      if (datum !== nextProps.data[index]) {
        hasDifferentData = true;
      }
    });

    const loadingStatusChanged = nextProps.loading !== state.loading;

    if (hasDifferentData || loadingStatusChanged) {
      return {
        data: nextProps.data,
        columns: nextProps.columns,
        loading: nextProps.loading,
        columnsCount: ((Object.keys(nextProps.columns)
          .filter((item) => nextProps.columns[item].visible)).length + 1),
        prevProps: nextProps,
      };
    }

    return null;
  }

  /* ===========================
  FUNCTIONS
  =========================== */
  /**
   * @param {event} event
   */
  onSearchColumnUpdate(event) {
    const { columns } = this.state;
    columns[event.target.name].search = (event.target.value.length > 0) ? event.target.value : '';
    this.props.onUpdatedColumns(columns, event.target.id);
  }

  /**
   * @param {Event} event
   */
  onSearchUpdate(event) {
    this.props.onUpdateSearch(event.target.value, event.target.id);
  }

  /**
   */
  onClickPrev() {
    const page = this.props.page - 1;
    this.props.onUpdatePage(page);
  }

  /**
   */
  onClickNext() {
    const page = this.props.page + 1;
    this.props.onUpdatePage(page);
  }

  /**
   * @param {Event} event
   * @param {integer} id
   */
  onClickRowOptions(event, id) {
    this.setState((prevState) => ({
      showOptionsRow: (prevState.showOptionsRow === id) ? null : id,
    }));

    event.stopPropagation();
  }

  /**
   * @param {Event} event
   * @param {integer} id
   */
  onClickRowCheckbox(event, id) {
    this.setState((prevState) => {
      let { selections } = prevState;

      if (selections?.length && selections?.indexOf(id) > -1) {
        selections = [
          ...selections.slice(0, selections.indexOf(id)),
          ...selections.slice((selections.indexOf(id) + 1)),
        ];
      } else if (!selections?.length) {
        selections = [id];
      } else {
        selections = [...selections, id];
      }

      let selection = (selections?.length > 0 && selections?.length === prevState.data.length) ? 'all' : 'some';
      selection = (selections?.length === 0) ? 'none' : selection;

      let { data } = this.state;
      data = data.filter((item) => (selections?.indexOf(item[this.props.uniqueKey]) > -1));
      data = (data.length > 0) ? data.map((item) => item[prevState.toCopy]) : [];

      return {
        clipboard: data.join(','),
        selection,
        selections,
      };
    });

    event.stopPropagation();
  }

  /**
   * @param {Event} event
   */
  onClickSelectionsDropdown() {
    this.setState((prevState) => ({
      showSelectionOptions: !prevState.showSelectionOptions,
    }));
  }

  /**
   * @param {Event} event
   */
  onClickSelectionCheckbox(event) {
    if (!this.state.loading && this.state.data.length > 0) {
      switch (this.state.selection) {
        case 'all':
          this.setState({
            selection: 'none',
            selections: [],
          });

          break;
        case 'some':
          this.setState({
            selection: 'none',
            selections: [],
          });

          break;
        case 'none':
          this.setState((prevState) => ({
            selection: 'all',
            selections: prevState.data.map((item) => (item[this.props.uniqueKey])),
          }));

          break;
        default:
          this.setState({
            selection: 'none',
            selections: [],
          });
          break;
      }
    }

    event.stopPropagation();
  }

  /**
   * @param {string} key
   */
  onClickSort(key) {
    if (this.state.showSortDirection
      && ((this.state.columns[key].sortable
      && this.state.columns[key].sortable === true) || !this.state.columns[key].sortable)) {
      this.props.onUpdateSortDirection(key);
    }
  }

  /* ===========================
  RENDER
  =========================== */
  /**
   * Main table render
   */
  renderTable() {
    return (
      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              {(this.props.optionsRows)
                ? (<th>&nbsp;</th>)
                : null}

              {(Object.keys(this.props.columns).map((col) => (
                (this.props.columns[col].visible)
                  ? (
                    <th className={`${((this.props.columns[col].sortable && this.props.columns[col].sortable === true) || !(this.props.columns[col].sortable)) ? 'sortable' : ''}`} onClick={() => { this.onClickSort(col); }} key={`data-header-${col}`}>
                      {(this.props.sortBy === col) ? (
                        <>
                          {(this.props.direction === 'asc') ? (<IconArrowDown />) : (<IconArrowUp />)}
                        </>
                      )
                        : null}
                      {(this.props.columns[col].label) ? this.props.columns[col].label : col}
                    </th>
                  )
                  : null
              )))}
            </tr>
            {(this.state.showSearchColumn)
              ? (
                <tr>
                  <th>&nbsp;</th>
                  {(Object.keys(this.props.columns).map((col) => (
                    (this.props.columns[col].visible)
                      ? (
                        <th key={`data-subheader-${col}`}>
                          <div className="form-group">
                            <input id={`Table-Search-Column-${col}`} disabled={this.state.loading} onChange={this.onSearchColumnUpdate} type="search" className="form-control" placeholder="Search" value={(this.props.columns[col]) ? this.props.columns[col].search : ''} name={col} />
                            <IconSearch />
                          </div>
                        </th>
                      )
                      : ''
                  )))}
                </tr>
              )
              : null}
          </thead>
          <tbody>
            {(this.state.loading)
              ? (<tr><td className="text-center" colSpan={this.state.columnsCount}><Loader /></td></tr>)
              : (
                <>
                  {(this.state.data.length > 0)
                    ? (this.state.data.map((row) => (
                      <tr key={`data-${row[this.props.uniqueKey]}`} className={`${(this.state.selections?.indexOf(row[this.props.uniqueKey]) > -1) ? 'selected' : ''}`} onClick={(event) => ((this.props.onClickRow) ? this.props.onClickRow(event, row) : null)}>
                        {(this.props.optionsRows)
                          ? (
                            <td>
                              <div className="btn-group" role="group" aria-label="Basic example">

                                <button type="button" onClick={(event) => this.onClickRowCheckbox(event, row[this.props.uniqueKey])} className="btn btn-light">
                                  {(this.state.selections?.indexOf(row[this.props.uniqueKey]) > -1)
                                    ? (<i><IconRadioChecked /></i>)
                                    : (<i><IconRadioUnchecked /></i>)}
                                </button>

                                {((this.props.optionsRows
                                  && Object.keys(this.props.optionsRows).length > 0)
                                  ? (
                                    <div className="btn-group" role="group">
                                      <button type="button" aria-label="click-row" className="btn btn-light" onClick={(event) => this.onClickRowOptions(event, row[this.props.uniqueKey])}><i className="fa fa-ellipsis-v" /></button>

                                      <div className={`dropdown-menu ${(!this.state.loading && this.state.showOptionsRow === row[this.props.uniqueKey]) ? 'show' : ''}`}>
                                        {(Object.keys(this.props.optionsRows)).map((item) => (
                                          <a key={`link-${item}`} className="dropdown-item" href={`#${item}`} onClick={(event) => { this.props.optionsRows[item].action(row); this.setState({ showOptionsRow: null }); event.stopPropagation(); event.preventDefault(); }}>{this.props.optionsRows[item].label}</a>))}
                                      </div>
                                    </div>
                                  )
                                  : ''
                                )}
                              </div>
                            </td>
                          )
                          : null}

                        {(Object.keys(this.props.columns).map((key) => (
                          <React.Fragment key={`fragment-${key}`}>
                            {(this.state.columns[key] && this.state.columns[key].visible)
                              ? (
                                <td key={`data-columns-${key}`}>
                                  {(this.state.columns[key].parent
                                    && this.state.columns[key].parent)
                                    ? (
                                      <>
                                        {(typeof row[this.state.columns[key].parent][key.replace(`${this.state.columns[key].parent}.`, '')] === 'string')
                                          ? (
                                            <>
                                              {row[this.state.columns[key].parent][key.replace(`${this.state.columns[key].parent}.`, '')]}
                                            </>
                                          )
                                          : JSON.stringify(row[this.state.columns[key].parent][key.replace(`${this.state.columns[key].parent}.`, '')])}
                                      </>
                                    )
                                    : (
                                      <>
                                        {(typeof row[key] === 'string')
                                          ? row[key]
                                          : JSON.stringify(row[key])}
                                      </>
                                    )}
                                </td>
                              )
                              : null}
                          </React.Fragment>
                        )))}
                      </tr>
                    )))
                    : (<tr><td colSpan={this.state.columnsCount}>No Results</td></tr>)}
                </>
              )}
          </tbody>

        </table>
      </div>
    );
  }

  /**
   * Main render
   */
  render() {
    return (
      <div className="Tables">
        {(this.state.showSearch)
          ? (
            <div className="Tables-Search">
              <div className="form-group">
                <input id="Table-Search-Input" onChange={this.onSearchUpdate} disabled={this.state.loading} type="search" className="form-control" placeholder="Search" value={this.props.search} />
                <IconSearch />
              </div>
            </div>
          )
          : null}

        <div className="Tables-Control">
          <div className="row">
            <div className="col col-8">
              {(this.state.showSelection)
                ? (
                  <div className="Table-Control-Selections float-left">
                    <div className="dropdown">
                      <button className="btn btn-light" type="button" onClick={this.onClickSelectionsDropdown} disabled={this.state.loading || this.state.data.length === 0}>
                        {(this.state.selection === 'none')
                          ? (<i onClick={this.onClickSelectionCheckbox}><IconRadioUnchecked /></i>)
                          : null}

                        {(this.state.selection === 'some')
                          ? (
                            <i onClick={this.onClickSelectionCheckbox}>
                              <IconRadioPartialChecked onClick={this.onClickSelectionCheckbox} />
                            </i>
                          )
                          : null}

                        {(this.state.selection === 'all')
                          ? (
                            <i onClick={this.onClickSelectionCheckbox}>
                              <IconRadioChecked onClick={this.onClickSelectionCheckbox} />
                            </i>
                          )
                          : null}

                        <span>Batch Actions</span>
                        <IconArrowDown />
                      </button>

                      <div className={`dropdown-menu ${(!this.state.loading && this.state.showSelectionOptions) ? 'show' : ''}`}>
                        {(Object.keys(this.props.optionsSelections).map((item) => (
                          (this.state.selections?.length > 0) ? (<a key={`link-${item}`} className="dropdown-item" href={`#${item}`} onClick={(event) => { this.props.optionsSelections[item].action(this.state.selections); this.setState({ showSelectionOptions: false }); event.preventDefault(); }}>{this.props.optionsSelections[item].label}</a>) : (<span key={`link-${item}`} className="dropdown-item">{this.props.optionsSelections[item].label}</span>)
                        )))}
                      </div>
                    </div>
                  </div>
                )
                : null}

              {(this.state.showLimit)
                ? (
                  <ButtonDropdownLimits
                    label={`Limit ${this.props.limit}`}
                    loading={this.state.loading}
                    classes="Tables-Control-Dropdown-Limits float-left"
                    options={this.props.optionsLimits}
                    onUpdateSelection={this.props.onUpdatedLimits}
                    optinonsStyle="btn-secondary"
                  />
                )
                : null}

              {(this.state.showPagination)
                ? (
                  <div className="Tables-Control-Pagination float-left">
                    <div className="btn-group" role="group">
                      <button type="button" disabled={this.state.loading || this.state.data.length === 0 || this.props.page === 1} onClick={this.onClickPrev} className="btn btn-info">Prev</button>
                      <button type="button" disabled={this.state.loading || this.state.data.length === 0 || ((this.props.meta.total - (this.props.page * this.props.limit)) <= 0)} onClick={this.onClickNext} className="btn btn-info">Next</button>
                    </div>
                  </div>
                )
                : null}

              {(this.state.showResults)
                ? (
                  <>
                    {(!this.state.loading)
                      ? (
                        <div className="Tables-Control-Showing">
                          {(this.props.meta.total)
                            ? (
                              <span className="Tables-Control-Showing-Results">
                                {(this.state.data.length > 0)
                                  ? (((this.props.page - 1) * this.props.limit) + 1)
                                  : 0}
                                -
                                {((this.state.data.length > 0)
                                  // eslint-disable-next-line max-len
                                  ? (((this.props.page - 1) * this.props.limit) + this.state.data.length)
                                  : 0)}
                                {' '}
                                of
                                {' '}
                                {this.props.meta.total}
                              </span>
                            )
                            : ''}
                        </div>
                      )
                      : null}
                  </>
                )
                : null}

            </div>
            <div className="col col-4">
              {(this.state.showColumns)
                ? (
                  <ButtonDropdownColumns
                    classes="Tables-Control-Dropdown-Columns float-right"
                    loading={this.state.loading}
                    label="Columns"
                    options={this.props.columns}
                    onUpdateChecks={this.props.onUpdateColumnsShowHide}
                  />
                )
                : null}

              {(this.props.optionsCustomButtons)
                ? (Object.keys(this.props.optionsCustomButtons).map((item) => (
                  <button type="button" disabled={this.state.loading} onClick={this.props.optionsCustomButtons[item].action} key={`custom-button-${item}`} className={`btn ${this.props.optionsCustomButtons[item].style}`}>
                    {this.props.optionsCustomButtons[item].label}
                  </button>
                )))
                : null}

            </div>
          </div>
        </div>
        {(this.state.selections?.length > 0) ? (
          <div className="Tables-Data-Selected">
            <small>
              <strong>{this.state.selections?.length}</strong>
              {' '}
              Rows Selected
            </small>
          </div>
        ) : ''}
        <div className="Tables-Data">
          {(this.renderTable())}
        </div>
      </div>
    );
  }
}

/* ===========================
PROPERTY TYPES
=========================== */
Tables.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  search: PropTypes.string,
  page: PropTypes.number,
  columns: PropTypes.object,
  sortBy: PropTypes.string,
  direction: PropTypes.string,
  meta: PropTypes.object,
  limit: PropTypes.number,
  uniqueKey: PropTypes.string,
  optionsShowSearch: PropTypes.bool,
  optionsShowSelection: PropTypes.bool,
  optionsShowPagination: PropTypes.bool,
  optionsShowResults: PropTypes.bool,
  optionsShowColumns: PropTypes.bool,
  optionsShowLimit: PropTypes.bool,
  optionsShowSearchColumn: PropTypes.bool,
  optionsSortDirection: PropTypes.bool,
  optionsRows: PropTypes.object,
  optionsCustomButtons: PropTypes.object,
  optionsLimits: PropTypes.object,
  optionsSelections: PropTypes.object,
  onUpdateColumnsShowHide: PropTypes.func,
  onUpdatedLimits: PropTypes.func,
  onUpdatedColumns: PropTypes.func,
  onUpdateSearch: PropTypes.func,
  onUpdatePage: PropTypes.func,
  onUpdateSortDirection: PropTypes.func,
  onClickRow: PropTypes.func,
};

/* ===========================
EXPORT
=========================== */
export default withRouter(Tables);
